import { EXPERT_VALIDATED_LABEL } from '@/shared/profile/expert-validation';
import type { FilterFieldDef } from '@/types/filters';

const TEXT_OPS = ['contains', 'not_contains', 'equals', 'not_equals', 'is_empty', 'is_not_empty', 'is_one_of', 'is_none_of', 'contains_one_of'] as const;
const COMPOSITE_TEXT_OPS = ['contains', 'not_contains', 'equals', 'not_equals', 'is_empty', 'is_not_empty'] as const;
const NUMBER_OPS = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'between'] as const;
const BOOLEAN_OPS = ['is_true', 'is_false'] as const;
const DATE_OPS = ['after', 'before', 'between'] as const;

export const PROFILE_FILTER_FIELDS: FilterFieldDef[] = [
  // ── Identity ──
  { field: 'full_name',        label: 'Name',         type: 'text',    operators: [...TEXT_OPS],    group: 'Identity' },
  { field: 'job_title',        label: 'Job Title',    type: 'text',    operators: [...TEXT_OPS],    group: 'Identity' },
  { field: 'headline',         label: 'Headline',     type: 'text',    operators: [...TEXT_OPS],    group: 'Identity' },
  { field: 'about',            label: 'About',        type: 'text',    operators: [...TEXT_OPS],    group: 'Identity' },

  // ── Array fields (types) ──
  { field: 'types',            label: 'Types',        type: 'text',    operators: ['contains', 'not_contains', 'is_empty', 'is_not_empty', 'is_one_of', 'is_none_of'], group: 'Identity' },

  // ── Primary type (#553/#554) ──
  // The ONE type a profile shows, and what the HNWIs band's figures apply. It
  // has to be declared here: the advanced filters drop any rule on a field
  // they do not know, so without this entry clicking a figure did nothing.
  { field: 'primary_type',     label: 'Primary type', type: 'text',    operators: ['equals', 'not_equals', 'is_one_of', 'is_none_of', 'is_empty', 'is_not_empty'], group: 'Identity' },

  // ── Custom tags (licence-scoped, user-defined) ──
  { field: 'custom_tags',      label: 'Custom Tags',  type: 'text',    operators: ['contains', 'not_contains', 'equals', 'not_equals', 'is_empty', 'is_not_empty', 'is_one_of', 'is_none_of', 'contains_one_of'], group: 'Custom Tags' },

  // ── Company ──
  { field: 'company',               label: 'Company',        type: 'text',   operators: [...TEXT_OPS],   group: 'Company' },
  { field: 'company_industry',      label: 'Industry',       type: 'text',   operators: [...TEXT_OPS],   group: 'Company' },
  { field: 'company_employee_count', label: 'Company Size',  type: 'number', operators: [...NUMBER_OPS], group: 'Company' },

  // ── Location (residence) ──
  { field: 'city',             label: 'City',         type: 'text',    operators: [...TEXT_OPS],    group: 'Location' },
  { field: 'country',          label: 'Country',      type: 'text',    operators: [...TEXT_OPS],    group: 'Location' },
  { field: 'continent',        label: 'Continent',    type: 'text',    operators: [...TEXT_OPS],    group: 'Location' },

  // ── Location (HQ) ──
  { field: 'hq_city',          label: 'HQ City',      type: 'text',    operators: [...TEXT_OPS],    group: 'HQ' },
  { field: 'hq_country',       label: 'HQ Country',   type: 'text',    operators: [...TEXT_OPS],    group: 'HQ' },

  // ── Contact reliability (#556) ──
  // One boolean per contact, not a three-value enum: "is this address reliable"
  // is the only question anyone asks of the column, and `is_false` deliberately
  // catches both "nobody checked it" and "there is none": the same reading the
  // nullable booleans above already have.
  { field: 'email_verified',   label: 'Email verified',  type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Contact' },
  { field: 'phone_verified',   label: 'Phone verified',  type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Contact' },
  // ── Contact presence (#615) ──
  // "Is there a value at all": the HNWIs Contact facet's question, which the
  // two fields above do not answer. An empty string counts as no value.
  { field: 'has_email',        label: 'Has email',       type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Contact' },
  { field: 'has_phone',        label: 'Has phone',       type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Contact' },
  { field: 'has_linkedin',     label: 'Has LinkedIn',    type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Contact' },

  // ── Network ──
  { field: 'connection_count', label: 'Connections',  type: 'number',  operators: [...NUMBER_OPS],  group: 'Network' },
  { field: 'follower_count',   label: 'Followers',    type: 'number',  operators: [...NUMBER_OPS],  group: 'Network' },

  // ── Experience ──
  { field: 'total_years_experience', label: 'Total Years of Experience', type: 'number', operators: [...NUMBER_OPS], group: 'Experience' },
  { field: 'current_company_join_year', label: 'Joined Company (Year)', type: 'number', operators: [...NUMBER_OPS], group: 'Experience' },
  { field: 'experience', label: 'Experience (relation)', type: 'experience', operators: [...COMPOSITE_TEXT_OPS], group: 'Experience' },

  // ── Education ──
  { field: 'school',           label: 'School',       type: 'text',    operators: [...TEXT_OPS],    group: 'Education' },
  { field: 'education',        label: 'Education (relation)', type: 'education', operators: [...COMPOSITE_TEXT_OPS], group: 'Education' },

  // ── Badges (#720) ──
  // Same label as the check beside the name (#719). Validated = date set.
  { field: 'is_expert_validated', label: EXPERT_VALIDATED_LABEL, type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Badges' },

  // ── CEO / Founder ──
  { field: 'is_ceo',           label: 'Is CEO',         type: 'boolean', operators: [...BOOLEAN_OPS], group: 'CEO' },
  { field: 'is_ex_ceo',        label: 'Former CEO',     type: 'boolean', operators: [...BOOLEAN_OPS], group: 'CEO' },
  { field: 'is_founder',       label: 'Is Founder',     type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Founder' },
  { field: 'is_ex_founder',    label: 'Former Founder', type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Founder' },

  // ── Investor ──
  { field: 'is_multiple_investor',    label: 'Multiple Investor',      type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Investor' },

  // ── Knowledge ──
  { field: 'has_real_estate_knowledge', label: 'Real Estate Knowledge', type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Knowledge' },
  { field: 'has_financial_knowledge',   label: 'Financial Knowledge',   type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Knowledge' },

  // ── Tracking ──
  { field: 'is_favorite',      label: 'Tracked',      type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Tracking' },

  // ── Source (#633/#634): what the client dashboard's figures open ──
  { field: 'from_invyo',       label: 'From Invyo Data', type: 'boolean', operators: [...BOOLEAN_OPS], group: 'Source' },
  { field: 'first_added_by_invyo_at', label: 'Added by Invyo', type: 'date', operators: [...DATE_OPS], group: 'Source' },

  // ── Dates ──
  { field: 'data_updated_at',  label: 'Last Updated', type: 'date',    operators: [...DATE_OPS],    group: 'Dates' },
  { field: 'created_at',       label: 'Created',      type: 'date',    operators: [...DATE_OPS],    group: 'Dates' },
];
